import { useEffect, useRef, useState, type ReactNode } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';
import { hashDeRuta } from '../lib/hash-route.js';
import { enZona, hora } from '../lib/fechas.js';
import { colorDe, iniciales } from '../lib/avatar.js';
import { ReunionAbierta, tituloReunion, type VistaReunion } from './ReunionDetalle.js';

/**
 * THE MEETINGS RECORDED WITH FLUXO PARA MEET, as a page of its own (#/reuniones-grabadas, and
 * #/reunion/<id> with one open; apptoolkit.fluxo.consulting/reuniones leads here). 27-oct, Joan
 * wanted them «en un entorno como el de Granola»; what was taken from Granola 7.668.3 (read with
 * REA): the list grouped by day («Hoy», «Ayer», then the date), each row with its title over the
 * people in it, and the open meeting on the right. The list comes a page at a time
 * (GET /api/reuniones-meet); without one chosen, the newest is open. Its search (⌘K, as Granola's)
 * asks the server, so it finds any meeting of the 90 days, also by what was said: the row then shows
 * the sentence where the first word is, the words marked.
 */

export interface FilaGrabacion extends Pick<VistaReunion, 'id' | 'codigo' | 'inicio' | 'fin' | 'estado' | 'resultado' | 'contacto' | 'notaEnGhl' | 'resumen'> {
  participantes: string[];
  /** In a search: the sentence of the transcript where its first word is. */
  fragmento?: string | null;
}

/** Without case or accents, one character for one (as the server's normalizarBusqueda). */
const TILDES_DESDE = 'ÁÀÄÂÃÅÉÈËÊÍÌÏÎÓÒÖÔÕÚÙÜÛÑÇáàäâãåéèëêíìïîóòöôõúùüûñç';
const TILDES_HASTA = 'aaaaaaeeeeiiiiooooouuuuncaaaaaaeeeeiiiiooooouuuunc';
const BASE = new Map([...TILDES_DESDE].map((c, i) => [c, TILDES_HASTA[i]]));
const normalizar = (c: string) => { const m = BASE.get(c) ?? c.toLowerCase(); return m.length === c.length ? m : c; };

/** The text with the words of the search marked, without case or accents. */
export function resaltar(texto: string, consulta: string): ReactNode[] {
  const t = [...texto];
  const plano = t.map(normalizar).join('');
  const marcas = new Array<boolean>(t.length).fill(false);
  for (const p of new Set([...consulta].map(normalizar).join('').split(/\s+/).filter(Boolean))) {
    for (let i = plano.indexOf(p); i >= 0; i = plano.indexOf(p, i + 1)) for (let j = i; j < i + [...p].length; j++) marcas[j] = true;
  }
  const out: ReactNode[] = [];
  let i = 0;
  while (i < t.length) {
    let j = i;
    while (j < t.length && marcas[j] === marcas[i]) j++;
    const trozo = t.slice(i, j).join('');
    out.push(marcas[i] ? <mark key={i}>{trozo}</mark> : trozo);
    i = j;
  }
  return out;
}
export interface PaginaGrabaciones { reuniones: FilaGrabacion[]; siguiente: string | null }

/** What a row says next to the time: where the recording is, or why it is not a meeting. */
export function etiquetaGrabacion(r: Pick<FilaGrabacion, 'estado' | 'resultado'>): string | null {
  if (r.estado === 'grabando') return 'Grabando';
  if (r.estado === 'subida') return 'Transcribiendo';
  if (r.estado === 'fallida') return 'Fallida';
  if (r.resultado === 'no_show') return 'No show';
  if (r.resultado === 'prueba') return 'Prueba';
  return null;
}

/** Under the title: the people Meet showed (when the title is the contact), or the room. */
export function subtituloFila(r: Pick<FilaGrabacion, 'contacto' | 'participantes' | 'codigo'>): string {
  return r.contacto && r.participantes.length ? r.participantes.join(', ') : `Google Meet ${r.codigo}`;
}

const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** The day a row is grouped under, in the tenant's zone: «Hoy», «Ayer» or «20 de octubre de 2026». */
export function diaDelGrupo(iso: string, zona: string, ahora: Date): string {
  const d = enZona(iso, zona)?.dia;
  if (!d) return '-';
  const hoy = enZona(ahora, zona)?.dia;
  const ayer = enZona(new Date(ahora.getTime() - 86_400_000), zona)?.dia;
  if (d === hoy) return 'Hoy';
  if (d === ayer) return 'Ayer';
  return `${Number(d.slice(8, 10))} de ${MESES_LARGOS[Number(d.slice(5, 7)) - 1]} de ${d.slice(0, 4)}`;
}

/** The rows by day, in the order they come (newest first). */
export function agruparPorDia(filas: FilaGrabacion[], zona: string, ahora: Date): Array<{ dia: string; filas: FilaGrabacion[] }> {
  const grupos: Array<{ dia: string; filas: FilaGrabacion[] }> = [];
  for (const f of filas) {
    const dia = diaDelGrupo(f.inicio, zona, ahora);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo?.dia === dia) ultimo.filas.push(f); else grupos.push({ dia, filas: [f] });
  }
  return grupos;
}

function paginaDesde(raw: unknown): PaginaGrabaciones | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<PaginaGrabaciones>;
  if (!Array.isArray(r.reuniones) || !r.reuniones.every((x) => x && typeof x.id === 'string' && typeof x.inicio === 'string')) return null;
  return { reuniones: r.reuniones.map((x) => ({ ...x, participantes: Array.isArray(x.participantes) ? x.participantes : [] })), siguiente: typeof r.siguiente === 'string' ? r.siguiente : null };
}

/**
 * `id`: the meeting open (from the address); null opens the newest. `inicial` and `abierta`: what
 * to show without fetching (tests and previews); the page fetches them otherwise.
 */
export function ReunionesGrabadas({ session, id = null, zona = 'Europe/Madrid', inicial = null, abierta = null, ahora }: {
  session: SessionState; id?: string | null; zona?: string; inicial?: PaginaGrabaciones | null; abierta?: VistaReunion | null; ahora?: Date;
}) {
  const [filas, setFilas] = useState<FilaGrabacion[] | null>(inicial?.reuniones ?? null);
  const [siguiente, setSiguiente] = useState<string | null>(inicial?.siguiente ?? null);
  const [cargando, setCargando] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // What is typed, and what is searched (a moment after the last key).
  const [consulta, setConsulta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const caja = useRef<HTMLInputElement>(null);
  // The meeting open stays open while a search loads (without one in the address, the newest).
  const ultimaAbierta = useRef<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setBusqueda(consulta.trim()), 250);
    return () => clearTimeout(t);
  }, [consulta]);

  // ⌘K / Ctrl+K: to the search.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); caja.current?.focus(); caja.current?.select(); }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, []);

  async function leer(antes: string | null): Promise<PaginaGrabaciones> {
    const q = new URLSearchParams();
    if (antes) q.set('antes', antes);
    if (busqueda) q.set('q', busqueda);
    const res = await authedFetch(session, `/api/reuniones-meet${q.toString() ? `?${q}` : ''}`);
    if (!res.ok) throw new Error(`No se pudieron cargar las reuniones grabadas (${res.status}).`);
    const p = paginaDesde(await res.json());
    if (!p) throw new Error('El servidor ha devuelto una lista que no se entiende.');
    return p;
  }

  useEffect(() => {
    if (inicial && !busqueda) return undefined;
    let cancelada = false;
    setFilas(null); setSiguiente(null); setErr(null);
    void (async () => {
      try {
        const p = await leer(null);
        if (!cancelada) { setFilas(p.reuniones); setSiguiente(p.siguiente); }
      } catch (e) {
        if (!cancelada) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelada = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, inicial, busqueda]);

  async function cargarMas() {
    if (!siguiente) return;
    setCargando(true);
    setErr(null);
    try {
      const p = await leer(siguiente);
      // A row already shown (a page read twice) is not repeated.
      setFilas((ya) => [...(ya ?? []), ...p.reuniones.filter((x) => !(ya ?? []).some((y) => y.id === x.id))]);
      setSiguiente(p.siguiente);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCargando(false);
    }
  }

  const abiertaId = id ?? filas?.[0]?.id ?? ultimaAbierta.current;
  ultimaAbierta.current = abiertaId;
  const previa = filas?.find((f) => f.id === abiertaId) ?? null;
  const grupos = agruparPorDia(filas ?? [], zona, ahora ?? new Date());

  return (
    // `sala--abierta`: one chosen in the address; on a phone, the meeting instead of the list.
    <div className={`sala${id ? ' sala--abierta' : ''}`}>
      <aside className="sala-lista" aria-label="Reuniones grabadas">
        <div className="sala-lista-cabeza">
          <a className="sala-atras sala-atras--panel" href="#">← Panel</a>
          <h1 className="sala-titulo">Reuniones</h1>
          <p className="sala-nota">Grabadas con Fluxo para Meet. Se borran a los 90 días.</p>
          <input
            ref={caja} className="sala-buscar" type="search" value={consulta} onChange={(e) => setConsulta(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setConsulta(''); }}
            placeholder="Buscar (⌘K)" aria-label="Buscar en las reuniones: contacto, nombres, resumen y lo que se dijo" maxLength={100}
          />
        </div>
        {!filas && !err && <p className="sala-vacio">{busqueda ? 'Buscando…' : 'Cargando…'}</p>}
        {filas?.length === 0 && <p className="sala-vacio">{busqueda ? `Ninguna reunión con «${busqueda}».` : 'Todavía no hay reuniones grabadas.'}</p>}
        {grupos.map((g) => (
          <section key={g.dia} className="sala-grupo">
            <h2 className="sala-grupo-titulo">{g.dia}</h2>
            {g.filas.map((r) => {
              const titulo = tituloReunion(r);
              const etiqueta = etiquetaGrabacion(r);
              const color = colorDe(titulo);
              return (
                <a
                  key={r.id} className={`sala-fila${r.id === abiertaId ? ' sala-fila--activa' : ''}`} href={hashDeRuta({ kind: 'reunion', id: r.id })}
                  aria-current={r.id === abiertaId ? 'page' : undefined}
                >
                  <span className="sala-fila-icono" style={{ background: `var(--av-${color})`, color: `var(--av-${color}-fg)` }} aria-hidden="true">{iniciales(titulo)}</span>
                  <span className="sala-fila-texto">
                    <span className="sala-fila-titulo">{titulo}</span>
                    {r.fragmento && busqueda
                      ? <span className="sala-fila-sub sala-fila-fragmento">{resaltar(r.fragmento, busqueda)}</span>
                      : <span className="sala-fila-sub">{subtituloFila(r)}</span>}
                  </span>
                  <span className="sala-fila-lado">
                    <span>{hora(r.inicio, zona)}</span>
                    {etiqueta && <span className="sala-chip">{etiqueta}</span>}
                  </span>
                </a>
              );
            })}
          </section>
        ))}
        {err && <p className="sala-aviso" role="alert">{err}</p>}
        {filas && siguiente && (
          <button className="sala-mas" type="button" onClick={() => void cargarMas()} disabled={cargando}>{cargando ? 'Cargando…' : 'Cargar más'}</button>
        )}
      </aside>
      <main className="sala-pagina">
        {abiertaId && <ReunionAbierta key={abiertaId} session={session} id={abiertaId} zona={zona} previa={previa} inicial={abierta?.id === abiertaId ? abierta : null} />}
      </main>
    </div>
  );
}
